import { Button } from "@/components/ui/button";
import { Check, Circle, Mic, ArrowRight, AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils";
import { valueOf } from "@/components/voice/topics";

const STEPS = ["Record", "Check", "Upload", "Review", "Generate"];

export function VoiceSteps({ at }) {
  return (
    <ol className="mx-auto mb-8 flex max-w-xl flex-wrap items-center justify-center gap-2 text-sm" data-testid="voice-steps">
      {STEPS.map((s, i) => (
        <li key={s} className={cn("flex items-center gap-1.5 rounded-full px-3 py-1 font-semibold", i < at ? "bg-[#00A85A]/10 text-[#008F4D]" : i === at ? "bg-[#00A85A] text-white" : "bg-black/5 text-[#8B9698] dark:bg-white/10")} data-testid={`voice-step-${s.toLowerCase()}`}>
          {i < at ? <Check className="h-3.5 w-3.5" /> : <span>{i + 1}</span>}{s}
        </li>
      ))}
    </ol>
  );
}

export function TopicList({ topics }) {
  return (
    <ul className="grid grid-cols-1 gap-1.5 text-left sm:grid-cols-2" data-testid="voice-topic-list">
      {topics.map((t) => (
        <li key={t.key} className="flex items-center gap-2 text-sm text-[#3a4a4f] dark:text-[#c8d2d4]">
          <Circle className="h-3 w-3 shrink-0 text-[#8B9698]" />{t.label}
          {t.mandatory && <span className="rounded bg-red-500/10 px-1.5 text-xs font-bold text-red-600" data-testid={`topic-mandatory-${t.key}`}>Required</span>}
        </li>
      ))}
    </ul>
  );
}

function MissingRow({ t, c, onAnswer, skipped, onSkip }) {
  return (
    <div className="rounded-xl border border-black/5 bg-white/80 p-3 dark:border-white/10 dark:bg-white/[0.04]" data-testid={`missing-${t.key}`}>
      <div className="mb-1.5 flex flex-wrap items-center gap-2 font-semibold text-[#0B1512] dark:text-white">{t.label}
        {t.mandatory ? <span className="rounded bg-red-500/10 px-1.5 text-xs font-bold text-red-600">Required</span> : <span className="text-xs text-[#8B9698]">Optional</span>}
      </div>
      {skipped ? <button type="button" onClick={() => onSkip(t.key, false)} className="text-sm font-semibold text-[#00A85A]" data-testid={`unskip-${t.key}`}>Skipped · answer instead</button> : (
        <div className="flex gap-2">
          <input value={c.voice_answers?.[t.key] || ""} onChange={(e) => onAnswer(t.key, e.target.value)} placeholder={t.hint || "Type the answer"} className="h-10 min-w-0 flex-1 rounded-lg border border-[#E2E8F0] bg-white px-3 dark:border-[#223139] dark:bg-white/5 dark:text-white" data-testid={`answer-${t.key}`} />
          {!t.mandatory && <Button type="button" variant="ghost" onClick={() => onSkip(t.key, true)} data-testid={`skip-${t.key}`}>Skip</Button>}
        </div>
      )}
    </div>
  );
}

export function VoiceCheck({ topics, c, onAnswer, onRecordAgain, onContinue, skipped, onSkip }) {
  const captured = topics.filter((t) => valueOf(t, c));
  const missing = topics.filter((t) => !valueOf(t, c));
  const req = missing.filter((t) => t.mandatory);
  const opt = missing.filter((t) => !t.mandatory);
  return (
    <div className="w-full text-left" data-testid="voice-check">
      <h2 className="text-center text-3xl font-extrabold tracking-tight text-[#0B1512] dark:text-white">Check what was captured</h2>
      <p className="mt-2 text-center text-[#8B9698]">Record again to fill gaps — nothing captured earlier is lost — or type answers below.</p>
      <div className="mt-6 grid gap-4 md:grid-cols-2">
        <section className="rounded-2xl border border-black/5 bg-white/70 p-4 dark:border-white/10 dark:bg-white/[0.04]" data-testid="voice-captured">
          <h3 className="mb-2 font-bold text-[#008F4D]">Captured ({captured.length})</h3>
          {captured.length ? <ul className="space-y-1.5">{captured.map((t) => <li key={t.key} className="flex gap-2" data-testid={`captured-${t.key}`}><Check className="mt-1 h-4 w-4 shrink-0 text-[#00A85A]" /><span><b>{t.label}:</b> <span className="break-words">{valueOf(t, c)}</span></span></li>)}</ul> : <p className="text-[#8B9698]">Nothing captured yet.</p>}
        </section>
        <section className="space-y-2" data-testid="voice-missing">
          <h3 className="font-bold text-red-600">Missing — required ({req.length})</h3>
          {req.length ? req.map((t) => <MissingRow key={t.key} t={t} c={c} onAnswer={onAnswer} onSkip={onSkip} />) : <p className="text-[#008F4D]">All required items are filled.</p>}
          {opt.length > 0 && <h3 className="pt-2 font-bold text-[#687173]">Missing — optional ({opt.length})</h3>}
          {opt.map((t) => <MissingRow key={t.key} t={t} c={c} onAnswer={onAnswer} skipped={skipped[t.key]} onSkip={onSkip} />)}
        </section>
      </div>
      <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
        <Button variant="outline" onClick={onRecordAgain} className="h-11 rounded-full px-6" data-testid="voice-record-again"><Mic className="mr-1.5 h-4 w-4" />Record again</Button>
        <Button onClick={onContinue} disabled={req.length > 0} className="h-11 rounded-full bg-[#00A85A] px-6 text-white hover:bg-[#008F4D]" data-testid="voice-check-continue">Continue to upload<ArrowRight className="ml-1.5 h-4 w-4" /></Button>
      </div>
      {req.length > 0 && <p className="mt-3 flex items-center justify-center gap-1.5 text-sm text-amber-700" data-testid="voice-required-note"><AlertTriangle className="h-4 w-4" />Fill {req.length} required item{req.length === 1 ? "" : "s"} to continue.</p>}
    </div>
  );
}
